import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Ban, Search, Edit, UserPlus, Trash2 } from "lucide-react";
import { formatDate } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { useAuthContext } from '@/contexts/AuthContext';
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface User {
  id: string;
  email: string;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  roles: {
    id: string;
    name: string;
    description?: string;
  }[];
  is_active: boolean;
}

interface UsersTableProps {
  onAddUser?: () => void;
  refreshTrigger?: number;
}

export default function UsersTable({ 
  onAddUser,
  refreshTrigger = 0
}: UsersTableProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkInactivateDialog, setShowBulkInactivateDialog] = useState(false);
  const itemsPerPage = 10;
  const { toast } = useToast();
  const { user: currentUser } = useAuthContext();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Primeiro, buscar dados dos usuários da tabela public.users
      const { data: publicUsers, error: publicError } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, created_at, updated_at, is_active, last_sign_in_at')
        .order('created_at', { ascending: false });

      if (publicError) {
        console.warn('❌ Error fetching public users:', publicError);
      }

      // Buscar roles dos usuários
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select(`
          user_id,
          roles (
            id,
            name,
            description
          )
        `);

      if (rolesError) {
        console.warn('Error fetching user roles:', rolesError);
      }

      // Processar todos os usuários da tabela public.users
      const allUsers = (publicUsers || []).map(user => {
        const userRoleData = userRoles?.filter(ur => ur.user_id === user.id) || [];
        const roles = userRoleData.map(ur => ur.roles).filter(Boolean).flat();
        
        const fullName = (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : null) ||
                        user.first_name || user.last_name || null;
        
        return {
          id: user.id,
          email: user.email || 'Email não disponível',
          full_name: fullName,
          first_name: user.first_name || null,
          last_name: user.last_name || null,
          created_at: user.created_at,
          last_sign_in_at: user.last_sign_in_at || null,
          email_confirmed_at: user.created_at,
          roles: roles,
          is_active: user.is_active ?? true // Usar o valor da coluna is_active
        };
      });

      console.log('📊 Total de usuários:', {
        ativos: allUsers.filter(u => u.is_active).length,
        inativos: allUsers.filter(u => !u.is_active).length,
        total: allUsers.length
      });

      setUsers(allUsers);
      applyFilters(allUsers, searchFilter, statusFilter);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar usuários',
        description: 'Não foi possível carregar a lista de usuários.',
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (data: User[], search: string, status: 'all' | 'active' | 'inactive' = statusFilter) => {
    let result = [...data];
    
    // Filtro por status
    if (status === 'active') {
      result = result.filter(user => user.is_active);
    } else if (status === 'inactive') {
      result = result.filter(user => !user.is_active);
    }
    
    // Filtro por busca
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(user => 
        user.email.toLowerCase().includes(searchLower) ||
        (user.full_name && user.full_name.toLowerCase().includes(searchLower)) ||
        (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
        (user.last_name && user.last_name.toLowerCase().includes(searchLower))
      );
    }
    
    setTotalPages(Math.max(1, Math.ceil(result.length / itemsPerPage)));
    setFilteredUsers(result);
    
    if (currentPage > Math.ceil(result.length / itemsPerPage)) {
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [refreshTrigger]);

  useEffect(() => {
    applyFilters(users, searchFilter, statusFilter);
  }, [searchFilter, statusFilter, currentPage]);

  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  };

  const handleDelete = async (id: string) => {
    try {
      // Inativar usuário marcando is_active = false
      const { error: updateError } = await supabase
        .from('users')
        .update({ is_active: false })
        .eq('id', id);

      if (updateError) {
        console.error('❌ Erro ao inativar usuário:', updateError);
        throw updateError;
      }

      toast({
        title: 'Usuário desativado',
        description: 'O usuário foi desativado com sucesso e não poderá mais fazer login.',
      });

      fetchUsers();
    } catch (error) {
      console.error('❌ Error deactivating user:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao desativar usuário',
        description: 'Não foi possível desativar o usuário. Por favor, tente novamente.',
      });
    }
  };

    const handleReactivate = async (id: string) => {
    try {
      // Reativar usuário marcando is_active = true
      const { error: updateError } = await supabase
        .from('users')
        .update({ is_active: true })
        .eq('id', id);

      if (updateError) {
        console.error('❌ Erro ao reativar usuário:', updateError);
        throw updateError;
      }

      toast({
        title: 'Usuário reativado',
        description: 'O usuário foi reativado com sucesso e poderá fazer login novamente.',
      });

      fetchUsers();
    } catch (error) {
      console.error('❌ Error reactivating user:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao reativar usuário',
        description: 'Não foi possível reativar o usuário. Por favor, tente novamente.',
      });
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditFirstName(user.first_name || '');
    setEditLastName(user.last_name || '');
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    setEditLoading(true);
    try {
      // Atualizar dados na tabela users
      const { error: publicError } = await supabase
        .from('users')
        .update({
          first_name: editFirstName || null,
          last_name: editLastName || null
        })
        .eq('id', editingUser.id);

      if (publicError) throw publicError;

      toast({
        title: 'Nome atualizado',
        description: 'O nome do usuário foi atualizado com sucesso.',
      });

      setEditingUser(null);
      setEditFirstName('');
      setEditLastName('');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar nome',
        description: 'Não foi possível atualizar o nome do usuário.',
      });
    } finally {
      setEditLoading(false);
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    const newSelection = new Set(selectedUsers);
    if (checked) {
      newSelection.add(userId);
    } else {
      newSelection.delete(userId);
    }
    setSelectedUsers(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = getPaginatedData()
        .filter(user => user.id !== currentUser?.id) // Não permitir selecionar o próprio usuário
        .map(user => user.id);
      setSelectedUsers(new Set(allIds));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleBulkInactivate = async () => {
    if (selectedUsers.size === 0) return;

    setBulkActionLoading(true);
    try {
      const userIds = Array.from(selectedUsers);
      

      
      const { error } = await supabase
        .from('users')
        .update({ is_active: false })
        .in('id', userIds);

      if (error) throw error;

      toast({
        title: `${userIds.length} usuário(s) inativado(s)`,
        description: 'Os usuários selecionados foram inativados com sucesso.',
      });

      setSelectedUsers(new Set());
      fetchUsers();
    } catch (error) {
      console.error('Error bulk inactivating users:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao inativar usuários',
        description: 'Não foi possível inativar os usuários selecionados.',
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkReactivate = async () => {
    if (selectedUsers.size === 0) return;

    setBulkActionLoading(true);
    try {
      const userIds = Array.from(selectedUsers);
      

      
      const { error } = await supabase
        .from('users')
        .update({ is_active: true })
        .in('id', userIds);

      if (error) throw error;

      toast({
        title: `${userIds.length} usuário(s) reativado(s)`,
        description: 'Os usuários selecionados foram reativados com sucesso.',
      });

      setSelectedUsers(new Set());
      fetchUsers();
    } catch (error) {
      console.error('Error bulk reactivating users:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao reativar usuários',
        description: 'Não foi possível reativar os usuários selecionados.',
      });
    } finally {
      setBulkActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="border rounded-lg p-4">
        <div className="flex items-center justify-center h-32">
          <p className="text-muted-foreground">Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <div className="flex justify-between items-center p-4 border-b">
        <h3 className="font-extrabold">Lista de Usuários</h3>
      </div>
      
      <div className="p-4 border-b">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="w-48">
            <Select value={statusFilter} onValueChange={(value: 'all' | 'active' | 'inactive') => setStatusFilter(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                    Todos os usuários
                  </div>
                </SelectItem>
                <SelectItem value="active">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    Apenas ativos
                  </div>
                </SelectItem>
                <SelectItem value="inactive">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    Apenas inativos
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Ações em Lote */}
      {selectedUsers.size > 0 && (
        <div className="p-4 bg-blue-50 border-l-4 border-blue-400 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-extrabold text-blue-800">
                {selectedUsers.size} usuário(s) selecionado(s)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkReactivate}
                disabled={bulkActionLoading}
                className="text-green-600 border-green-300 hover:bg-green-50"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Reativar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkInactivateDialog(true)}
                disabled={bulkActionLoading}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <Ban className="h-4 w-4 mr-2" />
                Inativar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedUsers(new Set())}
                className="text-gray-600"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">
              <Checkbox
                checked={
                  getPaginatedData().filter(user => user.id !== currentUser?.id).length > 0 &&
                  getPaginatedData()
                    .filter(user => user.id !== currentUser?.id)
                    .every(user => selectedUsers.has(user.id))
                }
                onCheckedChange={handleSelectAll}
                aria-label="Selecionar todos"
              />
            </TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Criado em</TableHead>
            <TableHead>Função</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-[100px]">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {getPaginatedData().length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground">
                Nenhum usuário encontrado.
              </TableCell>
            </TableRow>
          ) : (
            getPaginatedData().map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Checkbox
                    checked={selectedUsers.has(user.id)}
                    onCheckedChange={(checked) => handleSelectUser(user.id, checked as boolean)}
                    disabled={user.id === currentUser?.id}
                    aria-label={`Selecionar usuário ${user.email}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span className="font-extrabold">
                      {user.full_name || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : 'Nome não informado')}
                    </span>
                    {!user.full_name && (user.first_name || user.last_name) && (
                      <span className="text-xs text-muted-foreground">
                        {user.first_name ? `Nome: ${user.first_name}` : ''} {user.last_name ? `Sobrenome: ${user.last_name}` : ''}
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{formatDate(user.created_at)}</TableCell>
                <TableCell>
                  {user.roles.length > 0 
                    ? user.roles.map(role => role.name).join(', ')
                    : 'Sem função definida'
                  }
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      user.is_active ? 'bg-green-500' : 'bg-yellow-500'
                    }`}></div>
                  <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-extrabold ${
                    user.is_active 
                      ? 'bg-green-50 text-green-700' 
                      : 'bg-yellow-50 text-yellow-700'
                  }`}>
                    {user.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Abrir menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Ações</DropdownMenuLabel>
                      <DropdownMenuItem 
                        onClick={() => handleEditUser(user)}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Editar Nome
                      </DropdownMenuItem>
                      {user.is_active && user.id !== currentUser?.id && (
                        <DropdownMenuItem 
                          className="text-destructive"
                          onClick={() => handleDelete(user.id)}
                        >
                          <Ban className="mr-2 h-4 w-4" />
                          Inativar
                        </DropdownMenuItem>
                      )}
                      {user.is_active && user.id === currentUser?.id && (
                        <DropdownMenuItem 
                          className="text-muted-foreground"
                          disabled
                        >
                          <Ban className="mr-2 h-4 w-4" />
                          Não é possível inativar seu próprio usuário
                        </DropdownMenuItem>
                      )}
                      {!user.is_active && (
                        <DropdownMenuItem 
                          className="text-green-600"
                          onClick={() => handleReactivate(user.id)}
                        >
                          <UserPlus className="mr-2 h-4 w-4" />
                          Reativar
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      
      <div className="px-4 py-3 border-t bg-gray-50/50">
        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                      Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredUsers.length)} de {filteredUsers.length} usuários
                    </span>
                    {filteredUsers.length !== users.length && (
                      <span className="text-xs text-muted-foreground">
                        (filtrado de {users.length} total)
                      </span>
                    )}
                    <div className="flex items-center gap-3 text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="text-muted-foreground">
                          {users.filter(u => u.is_active).length} ativos
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                        <span className="text-muted-foreground">
                          {users.filter(u => !u.is_active).length} inativos
                        </span>
                      </div>
                    </div>
                  </div>
      
      {totalPages > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
              
                {/* Mostrar apenas algumas páginas se houver muitas */}
                {(() => {
                  const pagesToShow = [];
                  const delta = 2;
                  const start = Math.max(1, currentPage - delta);
                  const end = Math.min(totalPages, currentPage + delta);
                  
                  if (start > 1) {
                    pagesToShow.push(1);
                    if (start > 2) pagesToShow.push('...');
                  }
                  
                  for (let i = start; i <= end; i++) {
                    pagesToShow.push(i);
                  }
                  
                  if (end < totalPages) {
                    if (end < totalPages - 1) pagesToShow.push('...');
                    pagesToShow.push(totalPages);
                  }
                  
                  return pagesToShow.map((page, index) => (
                    <PaginationItem key={index}>
                      {page === '...' ? (
                        <span className="px-3 py-2 text-muted-foreground">...</span>
                      ) : (
                  <PaginationLink
                          onClick={() => setCurrentPage(page as number)}
                    isActive={currentPage === page}
                          className="cursor-pointer"
                  >
                    {page}
                  </PaginationLink>
                      )}
                </PaginationItem>
                  ));
                })()}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
          )}
        </div>
      </div>

      {/* Modal de Edição */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <Edit className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-xl font-extrabold">Editar Nome do Usuário</DialogTitle>
                <DialogDescription className="text-base mt-1">
                  Atualize o nome do usuário {editingUser?.email}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editFirstName" className="text-sm font-extrabold">
                Nome
              </Label>
              <Input
                id="editFirstName"
                type="text"
                value={editFirstName}
                onChange={(e) => setEditFirstName(e.target.value)}
                placeholder="Nome do usuário"
                className="h-11 text-base"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editLastName" className="text-sm font-extrabold">
                Sobrenome
              </Label>
              <Input
                id="editLastName"
                type="text"
                value={editLastName}
                onChange={(e) => setEditLastName(e.target.value)}
                placeholder="Sobrenome do usuário"
                className="h-11 text-base"
              />
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditingUser(null)}
              disabled={editLoading}
              className="h-11 px-6"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveEdit} 
              disabled={editLoading}
              className="h-11 px-8 gap-2"
            >
              {editLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <Edit className="h-4 w-4" />
                  Salvar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Inativação em Lote */}
      <AlertDialog open={showBulkInactivateDialog} onOpenChange={setShowBulkInactivateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Inativação em Lote</AlertDialogTitle>
            <AlertDialogDescription>
              Você está prestes a inativar <strong>{selectedUsers.size} usuário(s)</strong>.
              <br />
              Esta ação irá desabilitar o acesso destes usuários ao sistema.
              <br />
              <br />
              Você pode reativar os usuários posteriormente se necessário.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setShowBulkInactivateDialog(false);
                await handleBulkInactivate();
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Inativar {selectedUsers.size} Usuário(s)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 